import {
  Alert,
  AlertDescription,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { env } from "@monorepo/env/dashboard";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type StripeElementsOptions } from "@stripe/stripe-js";
import { useParams, useSearch } from "@tanstack/react-router";
import { CreditCard, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useIsDarkMode } from "@/hooks/use-is-dark-mode";
import { CheckoutForm } from "./checkout-form";
import { CheckoutPlanSummary } from "./components/checkout-plan-summary";

const stripePromise = loadStripe(env.VITE_STRIPE_PK ?? "");

const getStripeVariables = (isDark: boolean) => {
  const primaryColor = isDark ? "#10b981" : "#059669";
  return {
    colorPrimary: primaryColor,
    colorBackground: isDark ? "#292929" : "#ffffff",
    colorText: isDark ? "#ffffff" : "#262626",
    colorDanger: "#ef4444",
    fontFamily: '"GT Cinetype", system-ui, sans-serif',
    spacingUnit: "4px",
    borderRadius: "8px",
    colorTextSecondary: isDark ? "#a1a1a1" : "#6b7280",
    colorTextPlaceholder: isDark ? "#737373" : "#9ca3af",
    colorIconTab: isDark ? "#d4d4d8" : "#6b7280",
    iconColor: isDark ? "#d4d4d8" : "#6b7280",
    iconHoverColor: isDark ? "#e4e4e7" : "#525252",
    tabIconColor: isDark ? "#d4d4d8" : "#6b7280",
    tabIconHoverColor: isDark ? "#e4e4e7" : "#525252",
    tabIconSelectedColor: isDark ? "#ffffff" : "#262626",
    tabIconMoreColor: isDark ? "#d4d4d8" : "#6b7280",
    tabIconMoreHoverColor: isDark ? "#e4e4e7" : "#525252",
    focusBoxShadow: `0 0 0 2px ${primaryColor}`,
  };
};

const getInputRules = (isDark: boolean) => ({
  ".Input": {
    backgroundColor: isDark ? "#262626" : "#f8fafc",
    border: isDark ? "1px solid #404040" : "1px solid #e2e8f0",
  },
  ".Input:focus": {
    backgroundColor: isDark ? "#171717" : "#ffffff",
    borderColor: "#00FF67",
    boxShadow: "0 0 0 2px rgba(0, 255, 103, 0.2)",
  },
  ".Input:hover": {
    backgroundColor: isDark ? "#171717" : "#f1f5f9",
    borderColor: isDark ? "#525252" : "#cbd5e1",
  },
  ".Input--invalid": {
    boxShadow: "0 1px 1px 0 rgba(0, 0, 0, 0.07), 0 0 0 2px #ef4444",
  },
});

const getTabRules = (isDark: boolean) => ({
  ".Tab": {
    backgroundColor: isDark ? "#262626" : "#f8fafc",
    border: isDark ? "1px solid #404040" : "1px solid #E0E6EB",
    boxShadow: isDark
      ? "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 3px 6px rgba(0, 0, 0, 0.1)"
      : "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02)",
  },
  ".Tab:hover": {
    backgroundColor: isDark ? "#171717" : "#f1f5f9",
    color: isDark ? "#ffffff" : "#262626",
  },
  ".Tab--selected": {
    backgroundColor: isDark ? "#171717" : "#ffffff",
    borderColor: isDark ? "#404040" : "#E0E6EB",
    color: isDark ? "#ffffff" : "#262626",
    fontWeight: "600",
    boxShadow: isDark
      ? "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 3px 6px rgba(0, 0, 0, 0.1), 0 0 0 2px #10b981"
      : "0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(18, 42, 66, 0.02), 0 0 0 2px #059669",
  },
  ".Tab--selected:hover": {
    color: isDark ? "#ffffff" : "#262626",
  },
  ".Tab--selected:focus": {
    color: isDark ? "#ffffff" : "#262626",
  },
});

const getStripeRules = (isDark: boolean) => ({
  ...getInputRules(isDark),
  ...getTabRules(isDark),
  ".Label": {
    color: isDark ? "#ffffff" : "#262626",
    fontWeight: "500",
  },
  ".PickerItem": {
    backgroundColor: isDark ? "#262626" : "#f8fafc",
    color: isDark ? "#ffffff" : "#262626",
  },
  ".PickerItem:hover": {
    backgroundColor: isDark ? "#171717" : "#f1f5f9",
    color: isDark ? "#ffffff" : "#262626",
  },
});

const getStripeAppearance = (isDark: boolean) => ({
  theme: isDark ? "night" : "stripe",
  variables: getStripeVariables(isDark),
  rules: getStripeRules(isDark),
});

export const CheckoutPage = () => {
  const { planId } = useParams({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/billing/upgrade/checkout/$planId",
  });

  const search = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/billing/upgrade/checkout/$planId",
  });

  const { customerSessionSecret, paymentIntentSecret, setupIntentSecret } =
    search;
  const isDarkMode = useIsDarkMode();

  const stripeOptions = useMemo(() => {
    if (!setupIntentSecret) {
      return null;
    }

    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    return {
      appearance: getStripeAppearance(isDarkMode),
      clientSecret: paymentIntentSecret || setupIntentSecret,
      customerSessionClientSecret: customerSessionSecret,
    } as StripeElementsOptions;
  }, [
    customerSessionSecret,
    paymentIntentSecret,
    setupIntentSecret,
    isDarkMode,
  ]);

  // Show error state if no clientSecret is provided
  if (!(paymentIntentSecret || setupIntentSecret)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Alert className="max-w-md" variant="destructive">
          <AlertDescription>
            No checkout session found. Please return to the pricing page and try
            again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show loading state while Stripe options are being set up
  if (!stripeOptions) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-green-500" />
          <p className="text-muted-foreground">Setting up your checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto">
        {/* Main Content */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Payment Form - Left Column */}
          <div className="space-y-6 lg:col-span-2">
            {/* Payment Form */}
            <Card className="border-muted-foreground/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-green-500" />
                    <CardTitle>Payment Information</CardTitle>
                  </div>
                </div>
                <CardDescription>
                  Enter your payment details to complete the upgrade. You can
                  cancel anytime.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Elements options={stripeOptions} stripe={stripePromise}>
                  <CheckoutForm
                    paymentIntentSecret={paymentIntentSecret || ""}
                    planId={planId}
                    setupIntentSecret={setupIntentSecret || ""}
                  />
                </Elements>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary - Right Column */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <CheckoutPlanSummary planId={planId} />

              {/* Support Contact */}
              <Card className="mt-6 border-muted-foreground/10 bg-muted/20">
                <CardContent className="p-4">
                  <h3 className="mb-2 font-medium text-sm">Need Help?</h3>
                  <p className="mb-3 text-muted-foreground text-xs">
                    Our team is here to assist you with your upgrade.
                  </p>
                  <a
                    className="text-green-500 text-sm underline underline-offset-2 transition-colors hover:text-green-300"
                    href="mailto:sales@arcade.dev"
                  >
                    Contact Support
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
